/**
 * Custom `React.memo` equality check shared by every page's layout-mode
 * preview component.
 *
 * Why it exists:
 *   The default `React.memo` does a shallow `===` on every prop. That breaks
 *   here because parent pages frequently spread `dynamicCards` into a NEW
 *   array on every render — e.g. in every page:
 *       dynamicCards={[...(dynamicCards || []), ...(patientList?.customTabs || [])]}
 *   With the default memo, every keystroke / resize / RGL drag tick that
 *   re-renders the parent forces every preview to re-render, even though
 *   nothing they actually read from those props has changed. That single
 *   issue was the main source of drag-time reconciliation work.
 *
 * What we compare:
 *   - Identity of the card object (cheap fast path).
 *   - The card fields that actually drive the preview output
 *     (`id`, `component`, `type`, `width`, `title`).
 *   - For `dynamic` cards: the question-shape fields that choose which
 *     inner body to render (`data.type`, `data.templateType`,
 *     `data.isCustomDateAnalysis`, `data.customAnalysisType`), plus the
 *     resolved dynamic-card title from `dynamicCards` (because dynamic
 *     cards are the one case where a change to that array genuinely
 *     affects the displayed name).
 *
 * `sharedUsersByTabId` is compared by reference when present (same pattern
 * as hooks that replace the map object when share lists change).
 *
 * Everything else is ignored — in particular a fresh array reference for
 * `dynamicCards` is a no-op for non-dynamic cards.
 */
const arePreviewPropsEqual = (prev, next) => {
	const a = prev.card || {};
	const b = next.card || {};
	if (a === b) {
		if (prev.sharedUsersByTabId !== next.sharedUsersByTabId) return false;
		if (a.component === 'dynamic') {
			const prevDyn = (prev.dynamicCards || []).find((d) => d?.id === a.id);
			const nextDyn = (next.dynamicCards || []).find((d) => d?.id === a.id);
			if ((prevDyn?.title ?? null) !== (nextDyn?.title ?? null)) return false;
		}
		return true;
	}

	if (
		a.id !== b.id ||
		a.component !== b.component ||
		a.type !== b.type ||
		a.width !== b.width ||
		a.title !== b.title
	) {
		return false;
	}

	if (a.component === 'dynamic') {
		const ad = a.data || {};
		const bd = b.data || {};
		if (
			ad.type !== bd.type ||
			ad.templateType !== bd.templateType ||
			ad.isCustomDateAnalysis !== bd.isCustomDateAnalysis ||
			ad.customAnalysisType !== bd.customAnalysisType ||
			ad._id !== bd._id ||
			ad.isShared !== bd.isShared ||
			ad.isUniversalAccess !== bd.isUniversalAccess ||
			ad.isUniversal !== bd.isUniversal ||
			ad.shareInfo?.sharedBy?.fullName !== bd.shareInfo?.sharedBy?.fullName ||
			ad.sharedBy?.fullName !== bd.sharedBy?.fullName ||
			ad.sharedByName !== bd.sharedByName ||
			ad.ownerName !== bd.ownerName ||
			ad.createdBy?.fullName !== bd.createdBy?.fullName
		) {
			return false;
		}
		const prevDyn = (prev.dynamicCards || []).find((d) => d?.id === a.id);
		const nextDyn = (next.dynamicCards || []).find((d) => d?.id === a.id);
		if ((prevDyn?.title ?? null) !== (nextDyn?.title ?? null)) return false;
		if (prev.sharedUsersByTabId !== next.sharedUsersByTabId) return false;
	}

	return true;
};

export default arePreviewPropsEqual;
